import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
import { Bus, CalendarDays, Footprints, Hotel, Plane, Route, Train } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type {
  PublicItinerary,
  PublicItineraryNode,
  PublicItinerarySegment,
  PublicTimeSlot,
} from "../types";
import { ItineraryServiceAppendix, routeText, transportIconLabel } from "./ItineraryClientPreview";
import { requiresLongBusReview } from "../service";
import { buildDayNarratives } from "../service/itineraryOutputService";
import { itineraryPreviewImageUrl } from "../service/itineraryPreviewImageService";
import { formatTransportDuration, formatTransportTimeRange } from "../service/itineraryTransportTime";
import { ItineraryMarkdown, ItineraryMarkdownProvider } from "./ItineraryMarkdown";
import { ItineraryCustomerHeader, ItineraryHotelInfo } from "./ItineraryCustomerHeader";
import { canonicalPublicSegments, customerTransitSegmentsForDate, customerVisibleInterDaySegments } from "./publicItineraryPath";

type Props = {
  itinerary: PublicItinerary;
  initialDayNumber?: number;
  showDayNavigation?: boolean;
  internalActions?: ReactNode;
  idPrefix?: string;
  contentOnly?: boolean;
};

const internalPreviewScrollContainerSelector = ".itinerary-customization-panel-body, .arco-drawer-content";

/** 查找行程编辑器或预览抽屉内部的滚动容器，避免滚动冒泡到外层页面。 */
function findInternalPreviewScrollContainer(target: HTMLElement): HTMLElement | null {
  return target.closest<HTMLElement>(internalPreviewScrollContainerSelector);
}

/** 在预览内部定位日程卡片，并为顶部粘性日期导航预留空间。 */
function scrollDayWithinPreview(target: HTMLElement, article: HTMLElement | null): boolean {
  const container = article ? findInternalPreviewScrollContainer(article) : null;
  if (!container) return false;
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const navigationHeight = article?.querySelector<HTMLElement>(".itinerary-detail-nav")?.getBoundingClientRect().height ?? 0;
  const top = Math.max(0, container.scrollTop + targetRect.top - containerRect.top - navigationHeight - 8);
  if (typeof container.scrollTo === "function") container.scrollTo({ top, behavior: "auto" });
  else container.scrollTop = top;
  return true;
}

/** 将当前日期按钮居中到预览自己的横向导航条。 */
function centerDayNavigationButton(button: HTMLElement, navigation: HTMLElement | null): boolean {
  if (!navigation || navigation.scrollWidth <= navigation.clientWidth) return false;
  const left = Math.max(0, Math.min(
    button.offsetLeft - (navigation.clientWidth - button.offsetWidth) / 2,
    navigation.scrollWidth - navigation.clientWidth,
  ));
  if (typeof navigation.scrollTo === "function") navigation.scrollTo({ left, behavior: "auto" });
  else navigation.scrollLeft = left;
  return true;
}

const slots: PublicTimeSlot[] = ["morning", "afternoon", "evening", "unscheduled"];
const slotLabels: Record<PublicTimeSlot, string> = { morning: "上午", afternoon: "下午", evening: "晚上", unscheduled: "未分时" };
const nodeTypeLabels: Record<PublicItineraryNode["nodeType"], string> = {
  point: "考察点",
  railway_station: "火车站",
  airport: "机场",
  stay_area: "住宿",
  organization_site: "学校",
  custom: "考察点",
};
const transportLabels: Record<PublicItinerarySegment["transportMode"], string> = {
  rail: "火车",
  air: "飞机",
  bus: "大巴",
  walk: "步行",
  other: "其他交通",
};
const transportIcons = {
  rail: Train,
  air: Plane,
  bus: Bus,
  walk: Footprints,
  other: Route,
} as const;

/** 客户完整行程详情，按原型展示日期导航、实际路径与服务附录。 */
export function ItineraryClientDetail({
  itinerary,
  initialDayNumber,
  showDayNavigation = true,
  internalActions,
  idPrefix = "itinerary-detail",
  contentOnly = false,
}: Props) {
  const firstDay = itinerary.days[0]?.dayNumber;
  const [activeDay, setActiveDay] = useState(initialDayNumber ?? firstDay);
  const articleRef = useRef<HTMLElement | null>(null);
  const navigationRef = useRef<HTMLElement | null>(null);
  const pendingManualDay = useRef<number | null>(null);
  const dayNumbers = useMemo(() => new Set(itinerary.days.map((day) => day.dayNumber)), [itinerary.days]);
  // 日期定位只响应目标日变化或首次可用，不能因预览对象重建而打断工具栏输入。
  const initialTargetDay = initialDayNumber && dayNumbers.has(initialDayNumber) ? initialDayNumber : undefined;
  const trailingHotels = itinerary.hotels.filter((hotel) =>
    !hotel.dayNumbers.some((dayNumber) => dayNumbers.has(dayNumber)),
  );

  useEffect(() => {
    if (!initialTargetDay) return;
    pendingManualDay.current = initialTargetDay;
    setActiveDay(initialTargetDay);
    const daySection = document.getElementById(`${idPrefix}-day-${initialTargetDay}`);
    if (daySection && !scrollDayWithinPreview(daySection, articleRef.current)) {
      daySection.scrollIntoView?.({ block: "start" });
    }
  }, [idPrefix, initialTargetDay]);

  useEffect(() => {
    if (!showDayNavigation || activeDay === undefined) return;
    const navButton = document.getElementById(`${idPrefix}-nav-day-${activeDay}`);
    if (!navButton) return;
    centerDayNavigationButton(navButton, navigationRef.current);
  }, [activeDay, idPrefix, showDayNavigation]);

  useEffect(() => {
    if (!showDayNavigation || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting);
      if (!visible.length) {
        const firstSection = firstDay === undefined
          ? null
          : document.getElementById(`${idPrefix}-day-${firstDay}`);
        const firstSectionTop = firstSection?.getBoundingClientRect().top;
        if (pendingManualDay.current === null && firstSectionTop !== undefined && firstSectionTop >= 105) {
          setActiveDay(firstDay);
        }
        return;
      }
      if (pendingManualDay.current !== null) {
        const selectedVisible = visible.find((entry) => Number(entry.target.id.replace(`${idPrefix}-day-`, "")) === pendingManualDay.current);
        if (!selectedVisible) return;
        setActiveDay(pendingManualDay.current);
        pendingManualDay.current = null;
        return;
      }
      visible.sort((left, right) => Math.abs(left.boundingClientRect.top - 115) - Math.abs(right.boundingClientRect.top - 115));
      const number = Number(visible[0].target.id.replace(`${idPrefix}-day-`, ""));
      if (dayNumbers.has(number)) setActiveDay(number);
    }, { rootMargin: "-105px 0px -68% 0px", threshold: [0, 0.12, 0.35] });
    itinerary.days.forEach((day) => {
      const section = document.getElementById(`${idPrefix}-day-${day.dayNumber}`);
      if (section) observer.observe(section);
    });
    return () => observer.disconnect();
  }, [dayNumbers, firstDay, idPrefix, itinerary.days, showDayNavigation]);

  useEffect(() => {
    if (!showDayNavigation) return;
    const clearManualSelection = () => { pendingManualDay.current = null; };
    document.addEventListener("wheel", clearManualSelection, { capture: true, passive: true });
    document.addEventListener("touchstart", clearManualSelection, { capture: true, passive: true });
    document.addEventListener("pointerdown", clearManualSelection, true);
    return () => {
      document.removeEventListener("wheel", clearManualSelection, true);
      document.removeEventListener("touchstart", clearManualSelection, true);
      document.removeEventListener("pointerdown", clearManualSelection, true);
    };
  }, [showDayNavigation]);

  const scrollToDay = (dayNumber: number) => {
    pendingManualDay.current = dayNumber;
    setActiveDay(dayNumber);
    const daySection = document.getElementById(`${idPrefix}-day-${dayNumber}`);
    if (daySection && !scrollDayWithinPreview(daySection, articleRef.current)) {
      daySection.scrollIntoView({ behavior: "auto", block: "start" });
    }
  };

  return (
    <ItineraryMarkdownProvider itinerary={itinerary}>
    <article ref={articleRef} className="itinerary-client-sheet itinerary-client-detail">
      <ItineraryCustomerHeader itinerary={itinerary} internalActions={internalActions} titleOnly={contentOnly} />
      {showDayNavigation ? <nav ref={navigationRef} className="itinerary-detail-nav" aria-label="快速查看指定日期">
        {itinerary.days.map((day) => <button
          type="button"
          key={day.dayNumber}
          id={`${idPrefix}-nav-day-${day.dayNumber}`}
          aria-label={`D${day.dayNumber} ${detailNavTitle(day)}`}
          aria-current={activeDay === day.dayNumber ? "date" : undefined}
          onClick={() => scrollToDay(day.dayNumber)}
        ><strong>D{day.dayNumber}</strong></button>)}
      </nav> : null}
      {itinerary.days.length ? itinerary.days.map((day) => (
        <DetailDay
          idPrefix={idPrefix}
          key={day.dayNumber}
          day={day}
          contentOnly={contentOnly}
          hotels={itinerary.hotels.filter((hotel) => hotel.dayNumbers.includes(day.dayNumber))}
          transitSegments={customerTransitSegmentsForDate(itinerary.days, day.dayDate)}
        />
      )) : <p className="itinerary-output-empty">行程详情待补充</p>}
      {!contentOnly ? <>
        <ItineraryHotelInfo hotels={trailingHotels} ariaLabel="未关联天数酒店信息" />
        <ItineraryServiceAppendix itinerary={itinerary} />
      </> : null}
    </article>
    </ItineraryMarkdownProvider>
  );
}

/** 按“标题事实 -> 分时正文及详情 -> 考察点图片区 -> 交通事实”组织单日详情。 */
function DetailDay({ day, hotels, transitSegments, idPrefix, contentOnly }: { day: PublicItinerary["days"][number]; hotels: PublicItinerary["hotels"]; transitSegments: PublicItinerarySegment[]; idPrefix: string; contentOnly: boolean }) {
  const [failedMediaKeys, setFailedMediaKeys] = useState<Set<string>>(() => new Set());
  const tracks = day.parallelTracks ?? [];
  const allNodes = uniqueNodes([...day.nodes, ...tracks.flatMap((track) => track.nodes)]);
  const inspectionNodes = allNodes.filter(isInspectionNode);
  const mediaInspectionNodes = inspectionNodes.filter((node) => {
    const imageUrl = node.imageUrl?.trim();
    return Boolean(imageUrl && !failedMediaKeys.has(mediaNodeKey(node, imageUrl)));
  });
  const stayName = day.stayAreaName?.trim();
  const interDaySegments = customerVisibleInterDaySegments(day.segments);
  const routeGroups = [
    {
      title: tracks.length ? "公共交通" : "行程交通",
      segments: canonicalPublicSegments(day.nodes, day.segments),
    },
    ...tracks.map((track) => ({
      title: track.title,
      segments: canonicalPublicSegments(track.nodes, track.segments),
    })),
    { title: "全天在途", segments: transitSegments },
    { title: "跨日衔接", segments: interDaySegments },
  ].map((group) => ({ ...group, segments: group.segments.filter(shouldShowTransportFact) }))
    .filter((group) => group.segments.length > 0);
  const hasSummary = buildDayNarratives(day, inspectionNodes).length > 0;
  const isEmpty = !allNodes.length && !routeGroups.length && !hasSummary;
  const inspectionCount = inspectionNodes.length;
  const segmentCount = new Set(routeGroups.flatMap((group) => group.segments)).size;
  const nodeMeta = allNodes.length === 0
    ? (transitSegments.length > 0 ? "全天在途" : "交通日程")
    : inspectionCount > 0 ? `${inspectionCount} 个考察点` : `${allNodes.length} 个行程节点`;
  const isContentOnly = allNodes.length === 0 && segmentCount === 0 && hasSummary;

  return (
    <section className={`itinerary-detail-day${isEmpty ? " is-empty" : ""}`} id={`${idPrefix}-day-${day.dayNumber}`}>
      <header className="itinerary-detail-day-head" aria-label={`D${day.dayNumber} 日程标题`}>
        <span className={`itinerary-detail-day-badge${isEmpty ? " is-muted" : ""}`}>D{day.dayNumber}</span>
        <span className="itinerary-detail-day-date"><CalendarDays size={14} aria-hidden="true" />{formatDayDate(day.dayDate)}</span>
        <h2 className="itinerary-detail-day-title">{detailNavTitle(day)}</h2>
        <span className="itinerary-detail-day-stay"><Hotel size={14} aria-hidden="true" />住宿：{stayName || "待确认"}</span>
      </header>
      {!contentOnly ? <div className="itinerary-detail-day-meta sr-only">
        {isEmpty ? "尚未安排" : isContentOnly ? "内容日程" : <><span>{nodeMeta}</span><span>{segmentCount} 段交通</span></>}
      </div> : null}
      <div className="itinerary-detail-content">
        <DayNarratives day={day} inspectionNodes={inspectionNodes} />
        {!contentOnly && mediaInspectionNodes.length ? <section className="itinerary-detail-points" role="region" aria-label="考察点信息">
          <h3 className="sr-only">考察点信息</h3>
          {mediaInspectionNodes.map((node, index) => (
            <NodeDetail
              node={node}
              showDescription={false}
              requireImage
              onImageFailure={(imageUrl) => setFailedMediaKeys((current) => {
                const next = new Set(current);
                next.add(mediaNodeKey(node, imageUrl));
                return next;
              })}
              key={`${node.timeSlot}-${node.title}-${node.sortOrder}-${index}`}
            />
          ))}
        </section> : null}
        {!contentOnly ? <ItineraryHotelInfo hotels={hotels} ariaLabel={`D${day.dayNumber} 酒店信息`} /> : null}
        {!contentOnly ? routeGroups.map((group, groupIndex) => <section className="itinerary-detail-routes" role="region" aria-label={`${group.title}路线`} key={`${group.title}-${groupIndex}`}>
          <h4>{group.title}</h4>
          <ol className="itinerary-detail-transport-timeline" aria-label={`${group.title}步骤`}>
            {group.segments.map((segment, index) => <TransportFactCard segment={segment} key={`${segment.name}-${index}`} />)}
          </ol>
        </section>) : null}
        {isEmpty ? <p className="itinerary-output-empty">本日详细行程待补充</p> : null}
      </div>
    </section>
  );
}

/** 导航保留人工主题的短名称，仅把系统生成的英文标题替换为中文日次。 */
function detailNavTitle(day: PublicItinerary["days"][number]) {
  const title = day.dayTitle?.trim();
  if (!title || /^Day\s+\d+$/i.test(title)) return `第${day.dayNumber}天`;
  return title;
}

/** 日级兼容与本日摘要回退共用客户输出服务。 */
function DayNarratives({ day, inspectionNodes }: { day: PublicItinerary["days"][number]; inspectionNodes: PublicItineraryNode[] }) {
  return <div className="itinerary-detail-narratives">{buildDayNarratives(day, inspectionNodes).map(({ slot, paragraphs }) => {
    const label = slot === "unscheduled" ? "本日摘要" : slot === "full_day" ? "全天" : slotLabels[slot];
    return <section className="itinerary-detail-narrative" role="region" aria-label={`${label}正文`} key={slot}>
      {slot !== "unscheduled" ? <h3 className="itinerary-detail-narrative-heading">【{label}】</h3> : null}
      {paragraphs.map((part, index) => part.pointTitle ? <article
          className="itinerary-detail-point-description"
          aria-label={`${part.pointTitle}考察点详情`}
          key={index}
        >
          <ItineraryMarkdown content={part.content} />
        </article> : <ItineraryMarkdown key={index} className="itinerary-detail-narrative-copy" content={part.content} />)}
    </section>;
  })}</div>;
}

/** 合并公共节点与专业轨道节点，避免同一节点重复展示。 */
function uniqueNodes(nodes: PublicItineraryNode[]) {
  const seen = new Set<string>();
  return [...nodes]
    .sort((left, right) => slots.indexOf(left.timeSlot) - slots.indexOf(right.timeSlot) || left.sortOrder - right.sortOrder)
    .filter((node) => {
      const key = `${node.nodeType}:${node.timeSlot}:${node.title.trim()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/** 客户行程中的数据库点位和自定义考察点都归入考察点信息区。 */
function isInspectionNode(node: PublicItineraryNode) {
  return node.nodeType === "point" || node.nodeType === "custom";
}

/** 用公开节点身份和规范化图片地址隔离单日图片失败状态。 */
function mediaNodeKey(node: PublicItineraryNode, imageUrl: string) {
  return `${node.nodeType}:${node.timeSlot}:${node.sortOrder}:${node.title.trim()}:${imageUrl}`;
}

/** 将 ISO 日期转换为参考稿使用的中文月日与星期格式。 */
function formatDayDate(value?: string | null) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value?.trim() ?? "");
  if (!match) return value?.trim() || "日期待定";
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const weekdays = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"];
  return `${Number(month)}月${Number(day)}日（${weekdays[date.getUTCDay()]}）`;
}

/** 客户完整行程中的单个点位，图片只使用真实主数据或明确空态。 */
function NodeDetail({ node, showImage = true, showDescription = true, requireImage = false, onImageFailure }: { node: PublicItineraryNode; showImage?: boolean; showDescription?: boolean; requireImage?: boolean; onImageFailure?: (imageUrl: string) => void }) {
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const imageUrl = node.imageUrl?.trim() || null;
  const hasVisibleImage = Boolean(showImage && imageUrl && imageUrl !== failedImageUrl);
  const description = showDescription && isInspectionNode(node) ? node.description : null;
  const typeLabel = isInspectionNode(node) && (node.categoryLabel === "企业" || node.categoryLabel === "非遗")
    ? node.categoryLabel
    : nodeTypeLabels[node.nodeType];
  if (requireImage && !hasVisibleImage) return null;
  return <article className={`itinerary-detail-node ${hasVisibleImage ? "has-media" : "is-text-only"}`} aria-label={`${node.title}点位详情`}>
    {hasVisibleImage ? <div className="itinerary-detail-node-media"><img
      src={imageUrl ? resolveHttpResourceUrl(itineraryPreviewImageUrl(imageUrl, "small")) : undefined}
      alt={node.title}
      loading="lazy"
      decoding="async"
      ref={(image) => { if (image?.complete) updateDetailImageFit(image); }}
      onLoad={(event) => updateDetailImageFit(event.currentTarget)}
      onError={() => {
        if (!imageUrl) return;
        setFailedImageUrl(imageUrl);
        onImageFailure?.(imageUrl);
      }}
    /></div> : null}
    <div className="itinerary-detail-node-copy"><div className="itinerary-detail-node-title"><span className={`itinerary-node-type itinerary-node-type--${node.nodeType}`}>{typeLabel}</span><strong>{node.title}</strong></div>{description ? <ItineraryMarkdown content={description} /> : null}</div>
  </article>;
}

/** 渲染主交通事实与可选备用方案。 */
function TransportFactCard({ segment }: { segment: PublicItinerarySegment }) {
  const primary = segment.primaryTransport;
  const primaryIdentifier = transportIdentifierFact(segment);
  const TransportIcon = transportIcons[segment.transportMode];
  return <li className="itinerary-detail-transport-step" data-transport-mode={segment.transportMode}>
    <article className="itinerary-detail-transport" data-transport-mode={segment.transportMode} aria-label={`${segment.name}交通事实`}>
      <span className="itinerary-detail-transport-marker" role="img" aria-label={transportIconLabel(segment.transportMode)}><TransportIcon size={14} aria-hidden="true" /></span>
      <div className="itinerary-detail-transport-body" data-stay-endpoint={segment.hasStayEndpoint || undefined}>
        <span className="sr-only">{routeText(segment)}</span>
        <div className="itinerary-detail-transport-summary">
        <div className="itinerary-detail-transport-head"><strong>{segment.fromTitle && segment.toTitle ? `${segment.fromTitle} → ${segment.toTitle}` : segment.name}</strong></div>
        <div className="itinerary-detail-transport-facts">{segment.backupTransports.length ? <span className="itinerary-transport-main-tag">主交通</span> : null}{!segment.hasStayEndpoint ? <span className="itinerary-detail-transport-mode">{transportLabels[segment.transportMode]}</span> : null}{factLabel(primaryIdentifier)}{factLabel(primary?.seatClass)}{factLabel(primary?.cabinClass)}{!segment.hasStayEndpoint ? <>{factTime(segment.departureAt, segment.arrivalAt)}{factDistance(segment.distanceKm)}{factDuration(segment.durationMinutes)}</> : null}{segment.isPending ? <span className="itinerary-transport-pending-tag">待确认</span> : null}{!segment.hasStayEndpoint && requiresLongBusReview(segment) ? <span className="itinerary-transport-review-tag">超长大巴，待核对</span> : null}</div>
        </div>
        {segment.transportMode !== "walk" && segment.description ? <ItineraryMarkdown className="itinerary-detail-transport-description" content={segment.description} /> : null}
        {segment.backupTransports.length ? <div className="itinerary-detail-transport-backups"><span>备用交通</span>{segment.backupTransports.map((backup, index) => <div key={`${backup.routeCode || backup.label}-${index}`}><b>备用 {index + 1}</b>{factLabel(backup.routeCode || backup.label)}{factLabel(backup.seatClass)}{factLabel(backup.cabinClass)}{!segment.hasStayEndpoint ? <>{factTime(backup.departureAt, backup.arrivalAt)}{factDistance(backup.distanceKm)}{factDuration(backup.durationMinutes)}</> : null}{!segment.hasStayEndpoint && requiresLongBusReview({ transportMode: segment.transportMode, isPending: segment.isPending, distanceKm: backup.distanceKm, durationMinutes: backup.durationMinutes }) ? <span className="itinerary-transport-review-tag">超长大巴，待核对</span> : null}</div>)}</div> : null}
      </div>
    </article>
  </li>;
}

/** 详情预览与分享省略不足一个半小时的大巴接驳，保留边界及未知耗时事实。 */
function shouldShowTransportFact(segment: PublicItinerarySegment) {
  return segment.transportMode !== "bus"
    || segment.durationMinutes == null
    || !Number.isFinite(segment.durationMinutes)
    || segment.durationMinutes >= 90;
}

/** 极宽素材通常是品牌标识，完整展示比统一裁切更易识别。 */
function updateDetailImageFit(image: HTMLImageElement) {
  image.classList.toggle("is-contain", image.naturalHeight > 0 && image.naturalWidth / image.naturalHeight >= 2.4);
}

/** 默认工具标签若只是重复起终点则省略，车次、航班号等识别信息继续展示。 */
function transportIdentifierFact(segment: PublicItinerarySegment) {
  const value = segment.primaryTransport?.routeCode
    || segment.primaryTransport?.label
    || segment.routeCode;
  if (!value) return null;
  const normalized = normalizeRouteIdentity(value);
  const routeIdentities = [
    segment.name,
    segment.fromTitle && segment.toTitle ? `${segment.fromTitle} → ${segment.toTitle}` : null,
  ].filter((item): item is string => Boolean(item)).map(normalizeRouteIdentity);
  return routeIdentities.includes(normalized) ? null : value;
}

/** 归一路线身份中的连接符与空白，避免重复展示默认路线文案。 */
function normalizeRouteIdentity(value: string) {
  return value.replace(/（住宿区域）/g, "").replace(/[‐‑‒–—→]/g, "-").replace(/\s+/g, "").toLowerCase();
}

/** 仅在事实存在时输出文本标签。 */
function factLabel(value?: string | null) { return value ? <span>{value}</span> : null; }
/** 格式化已有出发/到达时间，缺失值不补占位。 */
function factTime(departure?: string | null, arrival?: string | null) {
  const value = formatTransportTimeRange(departure, arrival);
  return value ? <span>{value}</span> : null;
}
/** 仅在距离事实存在时输出距离。 */
function factDistance(distance?: string | null) { return distance?.trim() ? <span>{distance} 公里</span> : null; }
/** 仅在时长事实存在时输出时长。 */
function factDuration(minutes?: number | null) {
  const value = formatTransportDuration(minutes);
  return value ? <span>{value}</span> : null;
}
